<template>
    <div class="card card-default" :class="expand ? '' : 'project-collapsed'" @click="clickedProject">
        <div class="card-header">
            <div class="d-inline-block card-header-content">
                <div class="title">{{ project.name }} <span v-if="project.foreman && !expand" class="foreman">{{ project.foreman.name }}</span></div>

                <div v-if="isArchived">
                    <div v-if="expand && !isUnarchiving" class="link d-inline-block" @click="unarchive">Unarchive</div>
                    <div v-else-if="isUnarchiving" class="d-inline-block ml-2">
                        <div><span class="loader loader-xs mr-2"></span>Unearthing this project...</div>
                    </div>
                </div>
                <div v-else>
                    <div class="date" v-if="!isAdmin || overview">
                        <span v-tooltip.bottom="hoverDate(project.created_at)">Created {{ prettyDate(project.created_at) }}</span>

                        <div v-if="showNotificationOption" class="d-inline-block text-small">
                             • Default team notifications via <span :class="pendingNotificationOption ? '' : 'link'" @click="updateNotificationClicked">{{  notificationOption }}</span>
                        </div>
                    </div>

                    <!-- <div class="date" v-else>${{ project.runningTotal.finalTotal }}</div> -->
                    <div v-if="isOverdueToStart" class="overdue-warning" :class="warningClass">
                        <i class="fas fa-exclamation-triangle mr-1"></i> {{ overdueDaysString }} past start date ({{ startDate }})
                    </div>
                    <div v-else-if="showEstimatedStartDate" class="date">
                        Start date is in {{ daysUntilStartString }} ({{ startDate }})
                    </div>
                    <div v-if="isAdmin && expand" class="mt-1">
                        <div class="link d-inline-block" @click="toggleEdit" >{{ currentlyEditing ? 'Cancel' : 'Edit' }} </div>
                        <div v-if="isDeleting" class="d-inline-block ml-2">
                            <div><span class="loader loader-xs mr-2"></span>Archiving...</div>
                        </div>
                        <div v-else-if="!showingDeleteConfirmation" class="link d-inline-block ml-2" @click="showDeleteConfirmation">Archive</div>
                        <div v-else class="d-inline-block ml-2">
                            <span class="font-weight-600">Are you sure?</span>
                            <span class="clickable text-danger ml-2" @click="deleteProject">Yes</span>
                            <span class="link ml-2" @click="showingDeleteConfirmation = false">Cancel</span>
                        </div>
                        <div v-if="deleteStatusText" class='text-danger'>
                            {{ deleteStatusText }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="isArchived" class="d-inline-block float-right">
                <div class="badge badge-danger font-weight-600">Archived</div>
            </div>
            <div v-else class="d-inline-block float-right">
                <div v-if="isCustomer || isEmployee || !expand" class="float-right">
                    <span>
                        <i :class="statusButtonIcon"></i>
                    </span>
                    <span class="ml-2">{{ status }}</span>
                </div>
                <div v-else class="btn-group">
                  <button type="button" class="btn dropdown-toggle" :class="statusButtonClass" :disabled="savingStatus" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                    <span v-if="savingStatus" class="loader loader-sm float-left mr-2"></span>
                    <span v-else>
                        <i :class="statusButtonIcon"></i>
                    </span>
                    <span class="ml-2">{{ status }}</span>
                  </button>
                  <div class="dropdown-menu dropdown-menu-right">
                    <div class="dropdown-item" :class="status == 'Not started' ? '' : 'clickable'" @click="setStatus('Not started')"><i class="far fa-stop-circle"></i> <span class="ml-2">Not started</span></div>
                    <div class="dropdown-item" :class="status == 'In progress' ? '' : 'clickable'" @click="setStatus('In progress')"><i class="fas fa-tasks"></i> <span class="ml-2">In progress</span></div>
                    <div class="dropdown-item" :class="status == 'Complete' ? '' : 'clickable'" @click="setStatus('Complete')"><i class="far fa-check-circle"></i> <span class="ml-2">Complete</span></div>
                  </div>
                </div>
            </div>
            <div class="d-none d-md-block" v-if="showProgress">
            <div class="date d-block mt-2">
                <div class="progress-title mr-3">{{ project.totalNumberOfWorkOrders }} {{ pluralizeWorkOrders }}</div>
                    <div class="progress-container">
                        <div class="progress">
                            <div v-if="project.totalNumberOfWorkOrdersComplete > 0" class="progress-bar bg-success" role="progressbar" :style="completeWidth">{{ project.totalNumberOfWorkOrdersComplete }} complete</div>
                            <div v-if="project.totalNumberOfWorkOrdersInProgress > 0" class="progress-bar bg-secondary" role="progressbar" :style="inProgessWidth">{{ project.totalNumberOfWorkOrdersInProgress }} in progress</div>
                            <span v-if="showTotalNumberOfWorkOrdersRemaining" class="progress-text progress-bar bg-light text-dark" :style="remainingWidth">
                                {{ numberOfWorkOrdersNotStarted }} not started
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="error" class="text-danger ">Updating the project status failed. Please try again or get in touch!</div>
        </div>
        <!-- Editing -->
        <project-form v-if="currentlyEditing" v-bind:is-editing="true" :project="project"></project-form>
        <!-- Not editing -->
        <div v-else-if="expand">
            <div class="row">
                <div class="col-md-12">
                    <div class="map">
                        <g-map :address="project.fullAddress"></g-map>
                    </div>
                </div>
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col-md-6">
                        <div v-if="!isCustomer" class="row mb-2">
                            <div class="col-md-4 text-muted">
                                Customer
                            </div>
                            <div class="col-md-8">
                                <div v-if="project.customers.length == 0">
                                    <span class="link clickable" @click="addCustomer">Add customer</span>
                                </div>
                                <div v-else>
                                    <div v-for="customer in project.customers" :key="customer.id">
                                        {{ customer.name }} <a :href="'mailto:' + customer.email">(Email)</a>
                                        <br v-if="customer.phone" />
                                        <a :href="'tel:' + customer.phone">{{ customer.phone }}</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-md-4 text-muted">
                                Address
                            </div>
                            <div class="col-md-8">
                                {{ project.address }}
                                <br v-if="project.address2" />
                                {{ project.address2 }}
                                <br v-if="project.city" />
                                {{ project.city }}<span v-if="project.state">, </span>{{ project.state }}
                                <br v-if="project.country" />
                                {{ project.country }}
                                <br v-if="project.zip" />
                                {{ project.zip }}
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-md-4 text-muted">
                                Foreman
                            </div>
                            <div class="col-md-8" v-if="project.foreman">
                                {{ project.foreman.name }}
                                <br />
                                <a :href="'mailto:' + project.foreman.email">{{project.foreman.email}}</a>
                                <br v-if="project.foreman.phone" />
                                <a :href="'tel:' + project.foreman.phone">{{ project.foreman.phone }}</a>
                            </div>
                            <div class="col-md-6 text-disabled" v-else>
                                No foreman assigned
                            </div>
                        </div>

                        <div class="row mb-2" v-if="isAdmin">
                            <div class="col-md-4 text-muted">
                                Slack channel
                            </div>
                            <div class="col-md-8" v-if="!this.removingSlack && project.slack_webhook_channel">
                                <a :href="'https://app.slack.com/client/' + project.slack_team_id + '/' + project.slack_webhook_channel_id" target="_blank">{{ project.slack_webhook_channel }}</a>
                                    <span class="clickable remove-slack-button" @click="removeSlack">Remove</span>
                            </div>
                            <div class="col-md-8" v-else>
                                <a :href="'https://slack.com/oauth/authorize?scope=incoming-webhook&client_id=812058340871.810210378741&state=' + project.id + '&redirect_uri=https://sitecommandapp.com/redirect/slack/auth'">
                                    Connect to Slack channel
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6 border-left">
                        <div class="row mb-2">
                            <div class="col-md-6 text-muted">
                                Start date
                            </div>
                            <div class="col-md-6">
                                {{ startDate }}
                            </div>
                        </div>

                        <div class="row mb-2">
                            <div class="col-md-6 text-muted">
                                End date
                            </div>
                            <div class="col-md-6">
                                {{ endDate }}
                            </div>
                        </div>

                        <div class="row mb-2">
                            <div class="col-md-6 text-muted">
                                Job site access code
                            </div>
                            <div class="col-md-6">
                                {{ project.site_access_code }}
                            </div>
                        </div>

                        <div v-if="isAdmin || isCustomer" class="row mb-2">
                            <div class="col-md-6 text-muted">
                                Contract price
                            </div>
                            <div class="col-md-6">
                                ${{ project.formattedEstimate }}
                            </div>
                        </div>

                        <div v-if="isAdmin || isCustomer" class="row mb-2">
                            <div class="col-md-6 text-muted">
                                Tax
                            </div>
                            <div class="col-md-6">
                                {{ formattedTax }}%
                            </div>
                        </div>

                        <div v-if="!isCustomer" class="row mb-2">
                            <div class="col-md-6 text-muted">
                                Notes
                            </div>
                            <div class="col-md-12">
                                <span v-if="notes" class="multi-line">{{ notes }}</span>
                                <span v-else class="text-disabled">No notes</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: ['role', 'user', 'project', 'overview', 'expand'],

        created: function() {
            this.getBtnClassForStatus(this.project.status);
            //this.getCustomer();

            var vm = this;
            Bus.$on('new-project-canceled', function() {
                vm.currentlyEditing = false;
            });
            Bus.$on('project-updated', function(project) {
                if (project.id == vm.project.id) {
                    vm.currentlyEditing = false;
                }
            });
        },

        mounted: function() {

        },

        data: function() {
            return {
                error: false,
                savingStatus: false,
                statusButtonClass: 'btn-light',
                statusButtonIcon: 'fas fa-tasks',
                savingStatusText: 'Not started',
                //customerLoading:true,
                //customer: null,
                currentlyEditing: false,
                isDeleting: false,
                isUnarchiving: false,
                showingDeleteConfirmation: false,
                deleteStatusText: null,
                pendingNotificationOption: null,
                removingSlack: false
            };
        },

        watch: {
            // whenever question changes, this function will run
            project: function (newProject, oldProject) {
                if (newProject != oldProject) {
                    this.getBtnClassForStatus(newProject.status);
                    //this.getCustomer();
                }
            }
        },

        computed: {
            notificationOption: function() {
                if (this.pendingNotificationOption) return this.pendingNotificationOption;
                return this.projectNotificationOption;
            },

            projectNotificationOption: function() {
                if (this.project.notify_slack && this.project.notify_email) {
                    return 'Slack & email';
                }
                if (this.project.notify_slack) {
                    return 'Slack';
                }
                if (this.project.notify_email) {
                    return 'email';
                }
            },

            showNotificationOption: function() {
                if (!this.removingSlack && this.expand && this.isAdmin && this.project.slack_webhook_channel) {
                    return true;
                }
                return false;
            },

            warningClass: function() {
                if (this.daysSinceStart < 5) {
                    return "bg-info";
                }
                else if (this.daysSinceStart < 14) {
                    return "bg-warning";
                }
                return "bg-danger";
            },

            showProgress: function() {
                return !this.project.archived && this.project.status === 'In progress' && this.isAdmin && this.project.totalNumberOfWorkOrders > 1;
            },

            showEstimatedStartDate: function() {
                return this.project.status === 'Not started' && !this.expand && this.project.start_date;
            },

            inProgessWidth: function() {
                return "width: " + this.workOrderPercentageInProgess + "%; min-width:12%";
            },

            completeWidth: function() {
                return "width: " + this.workOrderPercentageComplete + "%; min-width:12%";
            },

            remainingWidth: function() {
                return "width: " + (100-(this.workOrderPercentageComplete + this.workOrderPercentageInProgess)) + "%";
            },

            numberOfWorkOrdersNotStarted: function() {
                return this.project.totalNumberOfWorkOrders - (this.project.totalNumberOfWorkOrdersComplete + this.project.totalNumberOfWorkOrdersInProgress);
            },

            showTotalNumberOfWorkOrdersRemaining: function() {
                //min space required to display the text:
                return this.workOrderPercentageComplete + this.workOrderPercentageInProgess < 80;
            },

            workOrderPercentageInProgess: function() {
                if (this.project.totalNumberOfWorkOrders == 0) return 0;
                return parseFloat(this.project.totalNumberOfWorkOrdersInProgress / this.project.totalNumberOfWorkOrders) * 100;
            },

            workOrderPercentageComplete: function() {
                if (this.project.totalNumberOfWorkOrders == 0) return 0;
                return parseFloat(this.project.totalNumberOfWorkOrdersComplete / this.project.totalNumberOfWorkOrders) * 100;
            },

            formattedTax: function() {
                // https://stackoverflow.com/questions/5623096/how-do-i-format-a-number-to-2-decimal-places-but-only-if-there-are-already-deci
                return Math.round(this.project.tax * 100) / 100;
            },

            isArchived: function () {
                return this.project.archived;
            },

            isCustomer: function() {
                return this.role === 'customer';
            },

            isEmployee: function() {
                return this.role === 'employee';
            },

            isAdmin: function() {
                return this.role === 'administrator' || this.role === 'owner';
            },

            startDate: function() {
                if (!this.project.start_date) {
                    return 'none';
                }

                return this.formatDate(this.project.start_date);
            },

            endDate: function() {
                if (!this.project.end_date) {
                    return 'none';
                }

                return this.formatDate(this.project.end_date);
            },

            notes: function() {
                return this.project.notes;
            },

            status: function() {
                if (this.savingStatus) {
                    return this.savingStatusText;
                }

                return this.project.status;
            },

            isOverdueToStart: function() {
                if (this.project.status === "Not started") {
                    return moment().diff(this.project.start_date, 'days') > 0;
                }

                return false;
            },

            daysSinceStart: function() {
                return moment().diff(this.project.start_date, 'days');
            },

            daysUntilStart: function() {
                return (this.daysSinceStart * -1) + 1;
            },

            daysUntilStartString: function() {
                let days = this.daysUntilStart;
                return String(days) + ' day' + (days == 1 ? '' : 's');
            },

            overdueDaysString: function() {
                let days = this.daysSinceStart;
                return String(days) + ' day' + (days == 1 ? '' : 's');
            },

            pluralizeWorkOrders: function() {
                if (this.project.totalNumberOfWorkOrders == 1) {
                    return "work order";
                }
                return "work orders";
            }
        },

        methods: {
            updateNotificationClicked: function(event) {

                if (this.pendingNotificationOption) {
                    return;
                }

                if (this.notificationOption === 'Slack & email') {
                    this.pendingNotificationOption = 'Slack';
                }
                else if (this.notificationOption === 'Slack') {
                    this.pendingNotificationOption = 'email';
                }
                else if (this.notificationOption === 'email') {
                    this.pendingNotificationOption = 'Slack & email';
                }

                this.updateNotification();
            },

            updateNotification: function() {
                axios.post(`/api/projects/${this.project.id}/notifications`, {
                            slack: this.pendingNotificationOption.toLowerCase().includes('slack'),
                            email: this.pendingNotificationOption.toLowerCase().includes('email')
                        })
                        .then(response => {
                            Bus.$emit('project-updated', response.data);
                            _.forEach(response.data.work_orders, workOrder => {
                                Bus.$emit('work-order-updated', workOrder);
                            });
                            this.pendingNotificationOption = null;
                        })
                        .catch(error => {
                            this.pendingNotificationOption = null;
                        });
            },

            removeSlack: function() {
                this.removingSlack = true;
                axios.get(`/api/projects/${this.project.id}/removeSlack`)
                    .then(response => {
                        this.removingSlack = false;
                        Bus.$emit('project-updated', response.data);
                    })
                    .catch(error => {
                        this.removingSlack = false;
                    });
            },

            clickedProject: function() {
                if (this.expand) return;
                Bus.$emit('set-current-project', this.project);
                $('a[href="#overview"]').trigger('click');
            },

            showDeleteConfirmation: function() {
                this.showingDeleteConfirmation = true;
            },

            toggleEdit: function() {
                this.currentlyEditing = !this.currentlyEditing;
            },

            hoverDate: function (date) {
                return moment.utc(date).local().format('MMMM DD YYYY h:mm A');
            },

            prettyDate: function (date) {
                return moment.utc(date).local().fromNow();
            },

            formatDate: function(date) {
                return moment(date).format('MMMM D YYYY');
            },

            getBtnClassForStatus: function(status) {
                if (status == 'Not started') {
                    this.statusButtonClass = 'btn-light';
                    this.statusButtonIcon = 'far fa-stop-circle'
                }
                else if (status == 'In progress') {
                    this.statusButtonClass = 'btn-light';
                    this.statusButtonIcon = 'fas fa-tasks'
                }
                else if (status == 'Complete') {
                    this.statusButtonClass = 'btn-light';
                    this.statusButtonIcon = 'far fa-check-circle'
                }
                else {
                    this.statusButtonClass = 'btn-light';
                    this.statusButtonIcon = 'fas fa-tasks'
                }
            },

            addCustomer: function() {
                Bus.$emit('set-current-project', this.project);
                $('a[href="#customer"]').trigger('click');
            },

            // getCustomer: function() {
            //     if (this.project.customer_id_1) {
            //         this.customerLoading = true;
            //         axios.get(`/api/customer/${this.project.customer_id_1}`)
            //             .then(response => {
            //                 this.customerLoading = false;
            //                 this.customer = response.data;
            //             })
            //             .catch(error => {
            //                 this.customerLoading = false;
            //             });
            //     }
            //     else {
            //         this.customer = null;
            //         this.customerLoading = false;
            //     }
            // },

            setStatus: function(status) {
                this.error = false;

                if (this.project.status != status) {
                    this.getBtnClassForStatus(status);
                    this.savingStatusText = status;
                    this.savingStatus = true;
                    axios.post(`/api/projects/${this.project.id}/status`, {
                            status: status
                        })
                        .then(response => {
                            this.savingStatus = false;
                            Bus.$emit('project-updated', response.data);
                            //Bus.$emit('refresh-current-project');
                        })
                        .catch(error => {
                            this.getBtnClassForStatus(this.project.status);
                            this.savingStatus = false;
                            this.error = true;
                        });
                }
            },

            deleteProject: function() {
                this.isDeleting = true;
                this.deleteStatusText = null;
                axios.delete(`/api/projects/${this.project.id}`)
                    .then(response => {
                        this.isDeleting = false;
                        this.showingDeleteConfirmation = false;
                        Bus.$emit('project-deleted', response.data);
                    })
                    .catch(error => {
                        this.isDeleting = false;
                        this.showingDeleteConfirmation = false;
                        this.deleteStatusText = 'We couldn\'t archive this project. Please try again or get in touch!';
                    });
            },

            unarchive: function() {
                this.isUnarchiving = true;
                this.deleteStatusText = null;
                axios.get(`/api/projects/${this.project.id}/unarchive`)
                    .then(response => {
                        this.isUnarchiving = false;
                        Bus.$emit('project-created', response.data);
                    })
                    .catch(error => {
                        this.isUnarchiving = false;
                    });
            }
        }
    }
</script>
